import React, { useState, useRef } from 'react';
import { Download, Share2, Copy, Check, RotateCcw, LogOut, Info, Key, Loader2, LayoutGrid, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { QRCode } from 'react-qrcode-logo';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { downloadSingleQR, convertQRToBlob } from '../../../utils/qrDownload';
import { useExperience } from '../../../stores/Experience/Experience';
import { axiosHelper, tokenHelper } from '../../../stores/helpers';
import { useAuth } from '../../../stores/Auth/Auth';
import { toast } from 'react-toastify';


const THEME_COLOR = '#c59253';

const AllSetStep = ({ state, result }) => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [tokenCopied, setTokenCopied] = useState(false);
    const terminology = useExperience.getState().getTerminology()
    // Default to the first space if available, otherwise fallback to a placeholder
    const [selectedSpaceIndex, setSelectedSpaceIndex] = useState(0);

    const spaceInfo = result?.space_info || [];
    const currentSpace = spaceInfo[selectedSpaceIndex] || { name: 'Demo Space', qrKey: 'demo' };
    const kitchenToken = result?.kitchen_token || 'KITCHEN_TOKEN_HERE';

    const qrBaseUrl = process.env.REACT_APP_QR_URL || 'https://clickorder.io';
    const qrValue = `${qrBaseUrl}/menu/${currentSpace.qrKey}`;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(qrValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopyToken = () => {
        navigator.clipboard.writeText(kitchenToken);
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
    };

    const reset = useExperience(state => state.reset);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const qrRefs = useRef({});
    const mainQRRef = useRef(null);

    const exitToDashboard = async () => {
        await axiosHelper.patch('/user/onboarding/complete', {}, {
            headers: {
                Authorization: `Bearer ${await tokenHelper.getToken()}`
            }
        });
        reset();

        await useAuth.getState().refreshUser();
        navigate('/')
    }

    const handleDownloadSingleQR = async (space) => {
        const qrElement = mainQRRef.current;
        if (!qrElement) return;
        const fileName = `QR_${space.name || 'Space'}.png`;
        await downloadSingleQR(qrElement, fileName, space.name);
    };

    const downloadAllQRs = async () => {
        if (spaceInfo.length === 0) return;
        setIsDownloadingAll(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            const zip = new JSZip();
            let successCount = 0;

            for (const space of spaceInfo) {
                const qrElement = qrRefs.current[space.id];
                if (!qrElement) continue;

                const blob = await convertQRToBlob(qrElement, space.name);
                if (blob) {
                    const fileName = `${space.name}_${space.id}.png`;
                    zip.file(fileName, blob);
                    successCount++;
                }
            }

            if (successCount === 0) {
                toast.error("Failed to generate any QR codes. Please try again.");
                return;
            }

            // CSV List
            const headers = ['Space Name', 'QR URL'];
            const csvRows = spaceInfo.map(space => [
                `"${(space.name || '').replace(/"/g, '""')}"`,
                `"${(qrBaseUrl + '/menu/' + space.qrKey || '').replace(/"/g, '""')}"`
            ].join(','));
            const csvContent = '\uFEFF' + [headers.join(','), ...csvRows].join('\n');
            zip.file("QR_Codes_List.csv", csvContent);

            const zipBlob = await zip.generateAsync({ type: "blob" });
            const siteName = state.siteInfo?.businessName || "QR_Codes";
            saveAs(zipBlob, `${siteName}_QR_Codes.zip`);
        } catch (error) {
            console.error("Error downloading QR codes:", error);
            toast.error("Failed to download QR codes. Please try again.");
        } finally {
            setIsDownloadingAll(false);
        }
    };

    const handleRestart = () => {
        reset();
        window.location.reload();
    };

    return (
        <div className="h-100 bg-white overflow-auto p-4 p-lg-5">
            <div className="container" style={{ maxWidth: '800px' }}>
                {/* Success Banner */}
                <div className="text-center mb-5 mt-4">
                    <div className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4" style={{ width: '64px', height: '64px', backgroundColor: THEME_COLOR }}>
                        <Check size={32} className="text-white" />
                    </div>
                    <h1 className="h3 mb-3">You're all set!</h1>
                    <p className="text-muted">
                        Your ordering system for <strong>{state.siteInfo?.businessName || 'your business'}</strong> is ready to use.
                    </p>
                </div>

                <div className="row g-4">
                    {/* Main QR Display */}
                    <div className="col-12 col-lg-7">
                        <div className="card border-0 shadow-sm bg-light rounded-4 h-100">
                            <div className="card-body p-4 d-flex flex-column align-items-center justify-content-center text-center">
                                <h3 className="h5 mb-4">QR Code</h3>
                                <div className="p-4 bg-white rounded-4 shadow-sm mb-4" ref={mainQRRef}>
                                    <QRCode
                                        value={qrValue}
                                        size={220}
                                        logoImage={state.siteInfo?.logoPreview || state.branding?.logo}

                                        eyeRadius={10}
                                        ecLevel="H"
                                        logoPaddingStyle="circle"
                                        bgColor="transparent"
                                        enableCORS={true}
                                        crossOrigin="anonymous"
                                    />
                                </div>

                                <div className="w-100 bg-white rounded-3 p-3 border mb-4 text-break">
                                    <div className="small text-muted mb-1 text-uppercase fw-bold" style={{ fontSize: '0.65rem' }}>Direct Link</div>
                                    <div className="d-flex align-items-center gap-2">
                                        <code className="flex-grow-1 text-dark">{qrValue}</code>
                                        <button onClick={handleCopyLink} className="btn btn-sm btn-light p-1">
                                            {copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="w-100">
                                    <button
                                        onClick={() => handleDownloadSingleQR(currentSpace)}
                                        className="btn text-white w-100 py-3 rounded-3 fw-bold shadow-sm"
                                        style={{ backgroundColor: THEME_COLOR }}
                                    >
                                        <Download size={18} className="me-2" />
                                        Download QR Image
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Spaces Selection List */}
                    <div className="col-12 col-lg-5">
                        <div className="d-flex flex-column h-100">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h4 className="h6 text-uppercase fw-bold text-muted mb-0 d-flex align-items-center gap-2">
                                    Your {terminology.spacePlural} <span className="badge rounded-pill bg-light text-muted">{spaceInfo.length}</span>
                                </h4>
                                <button
                                    onClick={downloadAllQRs}
                                    disabled={isDownloadingAll || spaceInfo.length === 0}
                                    className="btn btn-sm btn-link p-0 text-decoration-none d-flex align-items-center gap-1 fw-bold"
                                    style={{ color: THEME_COLOR }}
                                >
                                    {isDownloadingAll ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                    Download All (ZIP)
                                </button>
                            </div>
                            <div className="flex-grow-1 overflow-auto pe-2" style={{ maxHeight: '420px' }}>
                                <div className="d-flex flex-column gap-2">
                                    {spaceInfo.length > 0 ? spaceInfo.map((space, index) => (
                                        <div
                                            key={space.id || index}
                                            onClick={() => setSelectedSpaceIndex(index)}
                                            className={`card border cursor-pointer transition-all ${selectedSpaceIndex === index ? 'border-warning shadow-sm ring-1' : 'hover-bg-light'}`}
                                            style={{
                                                borderColor: selectedSpaceIndex === index ? THEME_COLOR : undefined,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <div className="card-body p-3 d-flex align-items-center justify-content-between">
                                                <div className="fw-bold text-dark">{space.name}</div>
                                                <div className="bg-light p-1 rounded-2">
                                                    <QRCode
                                                        value={`${qrBaseUrl}/menu/${space.qrKey}`}
                                                        size={40}
                                                        qrStyle="dots"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-5 bg-light rounded-4 border border-dashed">
                                            <p className="text-muted small mb-0">No spaces to display</p>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-4 pt-4 border-top">
                                    <div className="p-4 gap-4 rounded-4 border-2 border-dashed d-flex flex-column align-items-center justify-content-between bg-light">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="p-3 bg-white rounded-3 shadow-sm text-primary">
                                                <LayoutGrid size={24} />
                                            </div>
                                            <div>
                                                <h6 className="mb-1 fw-bold">Multiple {useExperience.getState().getTerminology().spacePlural.toLowerCase()}?</h6>
                                                <p className="text-muted small mb-0">
                                                    Need to create multiple {useExperience.getState().getTerminology().spacePlural.toLowerCase()} across branches?
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/space-management-onboarding')}
                                            className="btn btn-primary rounded-pill px-2 d-flex align-items-center justify-content-between gap-2 fw-bold shadow-sm w-100 text-center"
                                        >
                                            {useExperience.getState().getTerminology().business} Manager <ArrowRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kitchen Token Section  only show if service level is > 0*/}
                {state.serviceLevel > 0 && (

                    <div className="mt-5 p-4 rounded-4" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <div className="row align-items-center g-4">
                            <div className="col-md-7">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <div className="p-2 rounded-3 bg-white shadow-sm">
                                        <Key size={20} style={{ color: THEME_COLOR }} />
                                    </div>
                                    <h4 className="h5 mb-0">Kitchen Connection Token</h4>
                                </div>
                                <p className="text-muted small mb-3">
                                    Use this token to link your Kitchen Display System (KDS) or printer service with this site.
                                </p>
                                <div className="bg-white rounded-3 p-3 border d-flex align-items-center gap-3">
                                    <code className="flex-grow-1 fw-bold text-primary" style={{ fontSize: '1.1rem' }}>{kitchenToken}</code>
                                    <button
                                        onClick={handleCopyToken}
                                        className={`btn rounded-3 ${tokenCopied ? 'btn-success text-white' : 'btn-outline-secondary'}`}
                                    >
                                        {tokenCopied ? <Check size={18} /> : <Copy size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-5">
                                <div className="bg-white p-3 rounded-4 shadow-sm h-100">
                                    <h5 className="h6 mb-3 d-flex align-items-center gap-2">
                                        <Info size={16} className="text-info" />
                                        How to use:
                                    </h5>
                                    <ul className="list-unstyled small text-muted mb-0">
                                        <li className="mb-2">1. Open the <strong>Service Station App</strong></li>
                                        <li className="mb-2">2. Past the <strong>Kitchen Token</strong></li>

                                        <li className="mb-0">3. Start receiving <strong>orders</strong> </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Advanced Spaces Redirect */}

                {/* Final Actions */}
                <div className="d-flex flex-column flex-md-row gap-3 mt-5 pb-5">
                    <button
                        onClick={handleRestart}
                        className="btn btn-light border flex-grow-1 py-3 rounded-4 fw-bold text-muted d-flex align-items-center justify-content-center gap-2"
                    >
                        <RotateCcw size={20} />
                        Start New Experience
                    </button>
                    <button
                        onClick={async () => {

                            exitToDashboard()
                        }}
                        className="btn flex-grow-1 py-3 rounded-4 fw-bold text-white shadow-sm d-flex align-items-center justify-content-center gap-2"
                        style={{ backgroundColor: '#333' }}
                    >
                        <LogOut size={20} />
                        Exit to Dashboard
                    </button>
                </div>

                {/* Hidden QR codes for ZIP download */}
                <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
                    {spaceInfo.map((space) => (
                        <div
                            key={`qr-bulk-${space.id}`}
                            ref={(el) => (qrRefs.current[space.id] = el)}
                        >
                            <QRCode
                                value={`${qrBaseUrl}/menu/${space.qrKey}`}
                                size={512}
                                eyeRadius={10}
                                ecLevel="H"
                                logoPaddingStyle="circle"
                                bgColor="#fff"
                                logoImage={state.siteInfo?.logoPreview || state.branding?.logo}
                                enableCORS={true}
                                crossOrigin="anonymous"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AllSetStep;
